import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfigService, Page } from '@markant-platform/shared/data-access';
import { HttpClient } from '@angular/common/http';
import {
  NotificationCreateInput,
  PlatformAdminNotificationsData,
  ServiceAdminNotification,
  UserNotification,
} from './user-notifications.model';

@Injectable({ providedIn: 'root' })
export class UserNotificationsService {
  apiUrl: string = AppConfigService.appConfiguration.apiUrl['userNotificationsService'];

  constructor(private http: HttpClient) {}

  // * Get all user notifications
  getUserNotifications(
    pageIndex: number,
    pageSize: number,
    onlyUnread = false,
    periodFrom = '2020-01-01T00:00:00Z',
    periodTo = new Date().toISOString()
  ): Observable<Page<UserNotification>> {
    const url = encodeURI(
      `${this.apiUrl}?page=${pageIndex}&size=${pageSize}&onlyUnread=${onlyUnread}&periodFrom=${periodFrom}&periodTo=${periodTo}`
    );
    return this.http.get<Page<UserNotification>>(url);
  }

  // * Request a list of all user notifications for the platform admin.
  getPlatformAdminNotifications(pageIndex: number, pageSize: number): Observable<PlatformAdminNotificationsData> {
    const now: string = new Date().toISOString();
    const url = `${this.apiUrl}/platform-admin?page=${pageIndex}&size=${pageSize}&editableFrom=${now}`;
    return this.http.get<PlatformAdminNotificationsData>(url);
  }

  // * Request a list of all user notifications for the leistung admin.
  getServiceAdminNotifications(): Observable<Page<ServiceAdminNotification>> {
    const now: string = new Date().toISOString();
    const url = `${this.apiUrl}/leistung-admin` + '?editableFrom=' + now;
    return this.http.get<Page<ServiceAdminNotification>>(url);
  }

  // * Create Platform Admin Notification
  createPlatformAdminNotification(userNotificationData: NotificationCreateInput): Observable<UserNotification> {
    return this.http.post<UserNotification>(`${this.apiUrl}`, userNotificationData);
  }

  // * Create Service Admin Notification
  createServiceAdminNotification(userNotificationData: NotificationCreateInput): Observable<UserNotification> {
    return this.http.post<UserNotification>(`${this.apiUrl}/variants`, userNotificationData);
  }

  // * Mark single notification as read
  markUserNotificationAsRead(notificationId: string): Observable<UserNotification> {
    return this.http.post<UserNotification>(`${this.apiUrl}/read`, {
      notificationIds: [notificationId],
    });
  }

  // * Mark all notification as read
  markAllUserNotificationAsRead(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/read/all`, {});
  }

  // * Get count of all unread notifications
  getUnreadUserNotificationsCount(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/count`);
  }

  // * Delete multiple user notifications
  deleteUserNotifications(notificationIds: string[]): Observable<UserNotification> {
    const options = {
      body: {
        notificationIds: notificationIds,
      },
    };

    return this.http.delete<UserNotification>(`${this.apiUrl}`, options);
  }

  // * Delete user notification only for platform admins and leistung admin.
  deleteSingleUserNotification(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  getNotificationById(id: string): Observable<NotificationCreateInput> {
    return this.http.get<NotificationCreateInput>(`${this.apiUrl}/${id}`);
  }

  updatePlatformAdminNotification(id: string, data: NotificationCreateInput): Observable<NotificationCreateInput> {
    return this.http.put<NotificationCreateInput>(`${this.apiUrl}/${id}`, data);
  }

  updateServiceAdminNotification(id: string, data: NotificationCreateInput): Observable<NotificationCreateInput> {
    return this.http.put<NotificationCreateInput>(`${this.apiUrl}/variants/${id}`, data);
  }
}
